import { Button, Checkbox, IconButton, TextField } from '@ads/components-react';

import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExamPublication, ExamPublishStudent } from '../../../types/exam';

export interface AddStudentsBatchModalProps {
  readonly publication: ExamPublication | null;
  readonly availableStudents: readonly ExamPublishStudent[];
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (data: { publicationId: number; personIds: number[] }) => void;
}

export function AddStudentsBatchModal({
  publication,
  availableStudents,
  open,
  onClose,
  onConfirm,
}: AddStudentsBatchModalProps) {
  const { t } = useTranslation('examManagement');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const filtered = useMemo(() => {
    if (!search.trim()) return availableStudents;
    const q = search.toLowerCase();
    return availableStudents.filter(
      s => s.personName.toLowerCase().includes(q) || s.personCpf.includes(q),
    );
  }, [availableStudents, search]);

  const allSelected = filtered.length > 0 && filtered.every(s => selectedIds.has(s.personId));

  const toggleStudent = (personId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(s => s.personId)));
    }
  };

  const handleClose = () => {
    setSearch('');
    setSelectedIds(new Set());
    onClose();
  };

  const handleConfirm = () => {
    if (!publication || selectedIds.size === 0) return;
    onConfirm({
      publicationId: publication.id,
      personIds: Array.from(selectedIds),
    });
    setSearch('');
    setSelectedIds(new Set());
  };

  if (!publication) return null;

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-md py-xs">
            <Dialog.Title className="text-ctx-content-title ts-subtitle-sm font-semibold">
              {t('publications.addStudentsModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.cancel')}
              />
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-xxxs overflow-y-auto p-xs">
            <TextField
              fullWidth
              size="md"
              label={t('publications.addStudentsModal.search')}
              placeholder={t('publications.addStudentsModal.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />

            {filtered.length === 0 ? (
              <p className="ts-paragraph-xs text-ctx-content-title py-md text-center">
                {t('publications.addStudentsModal.noAvailable')}
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border pb-xs">
                  <Checkbox
                    label={t('publications.addStudentsModal.selectAll')}
                    checked={allSelected}
                    onCheckedChange={checked => {
                      if (checked !== 'indeterminate') {
                        toggleAll();
                      }
                    }}
                  />
                  <span className="ts-caption-xxs text-ctx-content-title">
                    {selectedIds.size} {t('publications.addStudentsModal.selected')}
                  </span>
                </div>

                <div className="flex max-h-giant flex-col gap-xxs overflow-y-auto">
                  {filtered.map(student => (
                    // biome-ignore lint/a11y/noLabelWithoutControl: label wraps ADS Checkbox that renders a native input internally
                    <label
                      key={student.personId}
                      className="flex cursor-pointer items-center gap-xs rounded-sm px-xs py-xxs transition-colors hover:bg-ctx-layout-hover"
                    >
                      <Checkbox
                        checked={selectedIds.has(student.personId)}
                        onCheckedChange={checked => {
                          if (checked !== 'indeterminate') {
                            toggleStudent(student.personId);
                          }
                        }}
                      />
                      <div className="flex flex-1 flex-col">
                        <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                          {student.personName}
                        </span>
                        <span className="ts-caption-xxs text-ctx-content-title">
                          {student.personCpf} — {student.personEmail}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-md py-xs">
            <Button hierarchy="secondary" size="sm" onClick={handleClose}>
              {t('actions.cancel')}
            </Button>
            <Button size="sm" onClick={handleConfirm} disabled={selectedIds.size === 0}>
              {t('publications.addStudentsModal.add')} ({selectedIds.size})
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
